import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SBINModal from "../modals/SBINModal";
import DeleteTicketModal from "../modals/DeleteTicketModal";
import "./MergeClientsModal.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
} from "../../ui/modalStyles";
import { digitsOnly, formatNANP } from "../../utils/phone";
import { mergeClients as mergeClientsApi } from "../../api/clients";

const HOUSE_ACCOUNT_NAME = "house account";

const isHouseAccountClient = (client) =>
  (client?.full_name || "").trim().toLowerCase() === HOUSE_ACCOUNT_NAME;

const formatClientName = (client) =>
  client?.full_name ||
  `${client?.first_name || ""} ${client?.last_name || ""}`.trim() ||
  "Unnamed Client";

const formatAddress = (client) => {
  const parts = [client?.address1, client?.address2].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
};

const formatCityState = (client) => {
  const parts = [client?.city, client?.state_province].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
};

function ClientDetails({ client }) {
  if (!client) {
    return (
      <div className="text-sm text-slate-500">Select a client to merge.</div>
    );
  }
  return (
    <div className="space-y-2 text-sm text-slate-700">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Name
        </div>
        <div className="font-semibold text-slate-900">
          {formatClientName(client)}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Address
        </div>
        <div>{formatAddress(client)}</div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          City & State
        </div>
        <div>{formatCityState(client)}</div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Mobile Phone
        </div>
        <div>{client.phone ? formatNANP(client.phone) : "-"}</div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Email
        </div>
        <div>{client.email || "-"}</div>
      </div>
    </div>
  );
}

ClientDetails.propTypes = {
  client: PropTypes.shape({
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state_province: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default function MergeClientsModal({
  isOpen,
  onClose,
  clients = [],
  targetClient,
  initialSourceClient = null,
  onMerged,
  mergeClients = mergeClientsApi,
}) {
  const [sourceClient, setSourceClient] = useState(null);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(true);
  const [error, setError] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmOpen(false);
      setConfirmBusy(false);
      setConfirmError("");
      return;
    }
    setSourceClient(initialSourceClient || null);
    setCurrentTarget(targetClient || null);
    setSearchTerm("");
    setSearchOpen(false);
    setError("");
    setIsConfirmOpen(false);
    setConfirmBusy(false);
    setConfirmError("");
  }, [isOpen, targetClient, initialSourceClient]);

  const eligibleClients = useMemo(() => {
    return (clients || []).filter(
      (client) =>
        client &&
        client.id !== currentTarget?.id &&
        !isHouseAccountClient(client)
    );
  }, [clients, currentTarget]);

  const term = searchTerm.trim().toLowerCase();
  const shouldShowResults = term.length > 0 && (searchOpen || !sourceClient);

  const filteredClients = useMemo(() => {
    if (!term) return [];
    const digits = digitsOnly(term);
    return eligibleClients
      .filter((client) => {
        const name = (client.full_name || "").toLowerCase();
        const email = (client.email || "").toLowerCase();
        const phone = digitsOnly(client.phone || "");
        return (
          name.includes(term) ||
          email.includes(term) ||
          (digits ? phone.includes(digits) : false)
        );
      })
      .slice(0, 8);
  }, [eligibleClients, term]);

  const handleSelectSource = (client) => {
    if (!client || client.id === currentTarget?.id) return;
    if (isHouseAccountClient(client)) {
      setError("House Account clients cannot be merged.");
      return;
    }
    setSourceClient(client);
    setSearchOpen(false);
    setSearchTerm("");
    setError("");
  };

  const handleClearSource = () => {
    setSourceClient(null);
    setSearchTerm("");
    setSearchOpen(false);
    setError("");
  };

  const handleSwap = () => {
    if (!sourceClient || !currentTarget) return;
    if (
      isHouseAccountClient(sourceClient) ||
      isHouseAccountClient(currentTarget)
    ) {
      setError("House Account clients cannot be merged.");
      return;
    }
    setCurrentTarget(sourceClient);
    setSourceClient(currentTarget);
    setSearchTerm("");
    setSearchOpen(false);
    setError("");
  };

  const handleOpenConfirm = () => {
    if (!sourceClient || !currentTarget) return;
    if (sourceClient.id === currentTarget.id) return;
    if (
      isHouseAccountClient(sourceClient) ||
      isHouseAccountClient(currentTarget)
    ) {
      setError("House Account clients cannot be merged.");
      return;
    }
    setConfirmError("");
    setIsConfirmOpen(true);
  };

  const handleConfirmMerge = async () => {
    if (!sourceClient || !currentTarget || confirmBusy) return;
    if (sourceClient.id === currentTarget.id) return;
    if (
      isHouseAccountClient(sourceClient) ||
      isHouseAccountClient(currentTarget)
    ) {
      setConfirmError("House Account clients cannot be merged.");
      return;
    }
    setConfirmBusy(true);
    setConfirmError("");
    try {
      await mergeClients({
        source_client_id: sourceClient.id,
        target_client_id: currentTarget.id,
      });
      await onMerged?.();
      setIsConfirmOpen(false);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setConfirmError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to merge clients."
      );
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <>
      <SBINModal
        isOpen={isOpen}
        onClose={onClose}
        title="Merge Clients"
        size="xxl"
        fitContent
      >
        <div className="space-y-6">
          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div className={LABEL_TEXT}>Merge this Client Record</div>
              <div className="merge-left">
                <div className="merge-search-row">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setSearchOpen(true);
                    }}
                    onFocus={() => setSearchOpen(true)}
                    placeholder="Search name, email, phone"
                    className={`merge-search-input w-full ${FIELD_CLASS}`}
                  />
                  {sourceClient && (
                    <button
                      type="button"
                      onClick={handleClearSource}
                      className="merge-search-change"
                    >
                      Change
                    </button>
                  )}
                </div>
                {shouldShowResults && (
                  <div className="merge-search-results">
                    {filteredClients.length === 0 && (
                      <div className="merge-no-results">
                        No matching clients.
                      </div>
                    )}
                    {filteredClients.map((client) => {
                      const isSelected = sourceClient?.id === client.id;
                      return (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectSource(client)}
                          className={`merge-result w-full rounded-xl border px-3 py-2 text-left text-sm ${
                            isSelected
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <div className="font-semibold">
                            {formatClientName(client)}
                          </div>
                          <div className="text-xs">
                            {client.phone ? formatNANP(client.phone) : "No phone"}{" "}
                            {client.email ? ` | ${client.email}` : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="merge-client-details">
                  {sourceClient ? (
                    <ClientDetails client={sourceClient} />
                  ) : (
                    <div className="merge-hint">
                      Type to search and select a client to merge.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center pt-10">
              <button
                type="button"
                onClick={handleSwap}
                className="h-11 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50"
                disabled={!sourceClient || !currentTarget}
              >
                Swap
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div className={LABEL_TEXT}>Into this Client Record</div>
              <ClientDetails client={currentTarget} />
            </div>
          </div>
          <div className="text-sm text-rose-600 font-semibold">
            IMPORTANT: If a field exists on both records, the right-side value
            remains unchanged.
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className={BTN_OUTLINE}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOpenConfirm}
              className={BTN_PRIMARY}
              disabled={!sourceClient || confirmBusy}
            >
              Merge
            </button>
          </div>
        </div>
      </SBINModal>
      <DeleteTicketModal
        isOpen={isConfirmOpen}
        onClose={() => {
          if (confirmBusy) return;
          setIsConfirmOpen(false);
          setConfirmError("");
        }}
        onConfirm={handleConfirmMerge}
        isSubmitting={confirmBusy}
        title="Confirm"
        message="Are you sure you want to merge this client?"
        subMessage="This action cannot be undone."
        detailMessage="This will move appointments, tickets, and notes and delete the merged record."
        confirmText="Merge"
        confirmBusyText="Merging..."
        errorText={confirmError}
      />
    </>
  );
}

MergeClientsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object),
  targetClient: PropTypes.object,
  initialSourceClient: PropTypes.object,
  onMerged: PropTypes.func,
  mergeClients: PropTypes.func,
};
